import Phaser from "phaser";
import { DynamicScene } from "./DynamicScene";
import { Global } from "../utils/Global";

export class LoadLevelScene extends DynamicScene {
  private loadingBar: Phaser.GameObjects.Graphics = null!;
  private loadingText: Phaser.GameObjects.BitmapText = null!;
  private loadingProgress: number = 0;

  constructor() {
    super({ key: "LoadLevelScene" });
  }

  override init(): void {
    super.init();
  }

  public preload(): void {
    this.load.setCORS("*");

    // Add error handling for asset loading
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      console.error(
        `Failed to load file '${file.key}' - ${file.xhrLoader?.statusText || "Unknown error"}`,
      );
    });
  }

  public create(): void {
    // Create loading bar and text
    this.loadingBar = this.add.graphics();
    this.loadingText = this.add.bitmapText(
      this.scale.width / 2,
      this.scale.height / 2,
      "fnt_paintBasic",
      "0%",
      Math.max(Global.minFontSize * 2, 20),
    );
    this.loadingText.setOrigin(0.5);
    this.drawgraphics();

    this.isReady = true;

    // Track progress on load start and update events
    this.load.on(
      "progress",
      (progress: number) => {
        this.loadingProgress = Phaser.Math.Clamp(progress, 0, 1);
        this.drawgraphics();

        // On complete play an animation with the loading bar and continue to main menu
        if (progress === 1) {
          this.tweens.add({
            targets: this.loadingText,
            y: { from: this.loadingText.y, to: this.scale.height / 2 },
            duration: 500,
            ease: "Power2",
          });

          this.tweens.add({
            targets: [this.loadingBar],
            alpha: { from: 1, to: 0 },
            duration: 500,
            ease: "Power2",
            hold: 500,
            onComplete: () => this.scene.start("GameScene"),
          });
        }
      },
      this,
    );

    // Load remaining assets
    this.loadAssets();
  }

  override repositionElements(_width?: number, _height?: number): void {
    if (!this.isReady) return;

    // Redraw the loading bar and reposition loading text
    this.drawgraphics();
  }

  private drawgraphics(): void {
    const barWidth = this.scale.width * 0.5;
    const barHeight = this.scale.height * 0.01;
    const barX = (this.scale.width - barWidth) / 2;
    const barY = this.scale.height / 2 - barHeight / 2;

    // Define a padding to make the background bar slightly larger
    const barPadding = this.scale.width * 0.005;
    const bgBarX = barX - barPadding;
    const bgBarY = barY - barPadding;
    const bgBarWidth = barWidth + barPadding * 2;
    const bgBarHeight = barHeight + barPadding * 2;

    // Update and reposition the loading percentage text
    this.loadingText.setText(`${Math.floor(this.loadingProgress * 100)}%`);
    this.loadingText.setPosition(
      this.scale.width / 2,
      barY + this.loadingText.displayHeight / 2 + barHeight,
    );

    // Clear and redraw the loading bar
    this.loadingBar.clear();

    // Draw text background
    this.loadingBar.fillStyle(0x908cc7, 1);
    this.loadingBar.fillRoundedRect(
      this.loadingText.x - this.loadingText.displayWidth * 0.5,
      this.loadingText.y - this.loadingText.displayHeight * 0.5,
      this.loadingText.displayWidth,
      this.loadingText.displayHeight,
      bgBarHeight / 2,
    );

    // Draw the background bar with rounded edges
    this.loadingBar.fillStyle(0x908cc7, 1);
    this.loadingBar.fillRoundedRect(
      bgBarX,
      bgBarY,
      bgBarWidth,
      bgBarHeight,
      bgBarHeight / 2,
    );

    // Draw the progress fill with rounded edges
    this.loadingBar.fillStyle(0xb0cfee, 1);
    this.loadingBar.fillRoundedRect(
      barX,
      barY,
      barWidth * this.loadingProgress,
      barHeight,
      barHeight / 2,
    );
  }

  private loadAssets(): void {
    this.loadTextures();
    this.loadAudio();
    this.loadFonts();

    // Otherwise, run the loader normally
    this.load.start();
  }

  private loadTextures(): void {
    // Load graphics here
    this.load.glsl(
      "shd_background",
      Global.shaderBaseUrl + "shd_background.frag",
    );

    this.load.atlas(
      "atlas_brake",
      Global.graphicsBaseUrl + "atlas_brake.png",
      Global.graphicsBaseUrl + "atlas_brake.json",
    );

    this.load.image("icon_shop", Global.graphicsBaseUrl + "icon_shop.png");

    this.load.image(
      "icon_opponent",
      Global.graphicsBaseUrl + "icon_opponent.png",
    );

    this.load.image("icon_boss", Global.graphicsBaseUrl + "icon_boss.png");

    /* TO BE CHANGED */
    this.load.image(
      "wheelPointer",
      Global.graphicsBaseUrl + "wheelPointer.png",
    );

    /* TO BE CHANGED */
    this.load.image("Button1", Global.graphicsBaseUrl + "Button01.png");
    this.load.image("Button2", Global.graphicsBaseUrl + "Button02.png");
    this.load.atlas(
      "DamagedSigns",
      Global.graphicsBaseUrl + "DamagedSigns.png",
      Global.graphicsBaseUrl + "DamagedSigns.json",
    );

    /* TO BE CHANGED */
    this.load.atlas(
      "items",
      Global.graphicsBaseUrl + "items.png",
      Global.graphicsBaseUrl + "items.json",
    );
  }

  private loadAudio(): void {
    // Load audio files here
    this.load.audio("sfx_wheelPegHit", [
      Global.audioBaseUrl + "sfx_wheelPegHit.ogg",
      Global.audioBaseUrl + "sfx_wheelPegHit.wav",
    ]);

    this.load.audio("sfx_brakeActivate", [
      Global.audioBaseUrl + "sfx_brakeActivate.ogg",
      Global.audioBaseUrl + "sfx_brakeActivate.wav",
    ]);

    this.load.audio("sfx_brakeAssemble", [
      Global.audioBaseUrl + "sfx_brakeAssemble.ogg",
      Global.audioBaseUrl + "sfx_brakeAssemble.wav",
    ]);

    this.load.audio("sfx_brakeDisassemble", [
      Global.audioBaseUrl + "sfx_brakeDisassemble.ogg",
      Global.audioBaseUrl + "sfx_brakeDisassemble.wav",
    ]);

    this.load.audio("song_1_loop_1", [
      Global.audioBaseUrl + "song_1/song_1_loop_1.wav",
    ]);

    this.load.audio("song_1_loop_2", [
      Global.audioBaseUrl + "song_1/song_1_loop_2.wav",
    ]);

    this.load.audio("song_1_loop_3", [
      Global.audioBaseUrl + "song_1/song_1_loop_3.wav",
    ]);

    this.load.audio("song_1_loop_4", [
      Global.audioBaseUrl + "song_1/song_1_loop_4.wav",
    ]);

    this.load.audio("song_1_loop_5", [
      Global.audioBaseUrl + "song_1/song_1_loop_5.wav",
    ]);

    this.load.audio("song_1_loop_6", [
      Global.audioBaseUrl + "song_1/song_1_loop_6.wav",
    ]);

    this.load.audio("song_1_loop_7", [
      Global.audioBaseUrl + "song_1/song_1_loop_7.wav",
    ]);

    this.load.audio("song_1_loop_8", [
      Global.audioBaseUrl + "song_1/song_1_loop_8.wav",
    ]);

    this.load.audio("song_1_loop_9", [
      Global.audioBaseUrl + "song_1/song_1_loop_9.wav",
    ]);

    this.load.audio("song_1_loop_10", [
      Global.audioBaseUrl + "song_1/song_1_loop_10.wav",
    ]);

    this.load.audio("song_1_loop_11", [
      Global.audioBaseUrl + "song_1/song_1_loop_11.wav",
    ]);
  }

  private loadFonts(): void {
    // Load additional fonts here
  }
}
